// ---------------------------------------------------------------------------
// useGraderVersions — fetch the immutable version snapshots for a single
// grader. Stays idle (empty, non-loading) until both `workspaceName` and
// `graderName` are non-null. Returns versions in the order the server provides
// (ascending). Built on the shared `useAsyncData` primitive for race-safe
// loading + refresh.
// ---------------------------------------------------------------------------

import { useMemo } from 'react';

import { useHttpClient } from '../../api/HttpClientContext.js';
import type { GraderVersion } from '../../services/grader.interface.js';
import { WorkflowApiService } from '../../services/workflowApi.js';
import { useAsyncData } from '../useAsyncData/useAsyncData.js';

import type {
  UseGraderVersionsParams,
  UseGraderVersionsResult,
} from './useGraderVersions.interface.js';

export function useGraderVersions(
  params: UseGraderVersionsParams,
): UseGraderVersionsResult {
  const { workspaceName, graderName } = params;
  const client = useHttpClient();
  const service = useMemo(() => new WorkflowApiService(client), [client]);

  const fetcher = useMemo<
    ((signal: AbortSignal) => Promise<GraderVersion[]>) | null
  >(() => {
    if (workspaceName === null || graderName === null) return null;
    return () => service.listGraderVersions(workspaceName, graderName);
  }, [service, workspaceName, graderName]);

  const { data, isLoading, error, refresh } = useAsyncData<GraderVersion[]>(
    fetcher,
    { errorMessage: 'Failed to load versions' },
  );

  return useMemo<UseGraderVersionsResult>(
    () => ({ versions: data ?? [], isLoading, error, refresh }),
    [data, isLoading, error, refresh],
  );
}
